import { TextTooltip } from '@components'
import { getDocumentsPageUrl, getInvoicingPageUrl } from '@constants'
import { PermissionSet, useBusinessContext, useFeature, usePermission } from '@context'
import { IDocument, IInvoice } from '@query'
import { DocumentFormModal } from '@pages/DocumentPage/DocumentForm'
import { CreateInvoiceModal } from '@pages/InvoicingPage/CreateInvoiceModal'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconType } from 'react-icons'
import { FaFileArrowDown, FaFileCirclePlus, FaFileInvoiceDollar, FaWallet } from 'react-icons/fa6'
import { FaQuestionCircle } from 'react-icons/fa'
import { Link, useHistory } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { CardSurface } from './shared/Card'
import { WHEN_NARROW } from '../layout'

type FormKind = 'invoice' | 'document'

interface QuickAction {
  key: string
  icon: IconType
  isAllowed: boolean
  to?: string
  form?: FormKind
}

export const QuickActions: React.FC = () => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const history = useHistory()
  const canCreateInvoice = usePermission(PermissionSet.INVOICING_EDIT)
  const canCreateDocument = usePermission(PermissionSet.BOOKKEEPING_EDIT)
  const canViewReporting = usePermission(PermissionSet.REPORTING_VIEW)
  const canUseSalary = usePermission(PermissionSet.SALARY)
  const { enabled: salariesEnabled } = useFeature('salaries')
  const [openForm, setOpenForm] = useState<FormKind | null>(null)

  const actions = (
    [
      {
        key: 'invoice',
        icon: FaFileInvoiceDollar,
        form: 'invoice',
        isAllowed: canCreateInvoice
      },
      {
        key: 'document',
        icon: FaFileCirclePlus,
        form: 'document',
        isAllowed: canCreateDocument
      },
      {
        key: 'salary',
        icon: FaWallet,
        to: `/${slug}/salaries?p=${encodeURIComponent('/calc/draft')}`,
        isAllowed: salariesEnabled && canUseSalary
      },
      {
        key: 'report',
        icon: FaFileArrowDown,
        to: `/${slug}/reporting`,
        isAllowed: canViewReporting
      }
    ] satisfies QuickAction[]
  ).filter(action => action.isAllowed)

  if (actions.length === 0) return null

  const closeForm = () => setOpenForm(null)
  const openCreatedInvoice = (invoice?: IInvoice) => {
    closeForm()
    if (invoice?.id) {
      history.push(getInvoicingPageUrl(slug, { id: invoice.id, type: 'sales' }))
    }
  }
  const openCreatedDocument = (document: IDocument) => {
    history.push(getDocumentsPageUrl(slug, { id: document.id }))
  }
  return (
    <Surface>
      <TitleRow>
        <Title>{t('dashboard.quickActions.title')}</Title>
        <TextTooltip tooltip={t('dashboard.quickActions.tooltip')} placement="bottom">
          <HelpIcon />
        </TextTooltip>
      </TitleRow>
      <ActionRow>
        {actions.map(({ key, icon: Icon, to, form }) =>
          to ? (
            <ActionLink key={key} to={to}>
              <Icon />
              <span>{t(`dashboard.quickActions.action.${key}`)}</span>
            </ActionLink>
          ) : (
            <ActionButton key={key} type="button" onClick={() => form && setOpenForm(form)}>
              <Icon />
              <span>{t(`dashboard.quickActions.action.${key}`)}</span>
            </ActionButton>
          )
        )}
      </ActionRow>

      {openForm === 'invoice' && (
        <CreateInvoiceModal onClose={closeForm} onSubmit={openCreatedInvoice} />
      )}

      <DocumentFormModal
        isVisible={openForm === 'document'}
        onClose={closeForm}
        onCreated={openCreatedDocument}
        defaults={{ date: dayjs().format('YYYY-MM-DD') }}
        enableCache={true}
      />
    </Surface>
  )
}

const Surface = styled(CardSurface)`
  min-width: 0;
  height: auto;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const Title = styled.span`
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.iconGray};
  white-space: nowrap;
`

const HelpIcon = styled(FaQuestionCircle)`
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  cursor: help;
`

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm}rem;

  > * {
    flex: 1 1 11rem;
    min-width: min(100%, 11rem);
  }

  ${WHEN_NARROW} {
    gap: ${({ theme }) => theme.spacing.xs}rem;

    > * {
      flex: 1 1 calc(50% - ${({ theme }) => theme.spacing.xs}rem);
      min-width: min(100%, 8rem);
    }
  }
`

const actionStyles = css`
  display: flex;
  flex: 1 1 0;
  min-width: 0;

  ${WHEN_NARROW} {
    gap: ${({ theme }) => theme.spacing.xs}rem;
    padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.xs}rem;
    font-size: ${({ theme }) => theme.fontSize.xs}rem;
  }

  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.sm}rem;
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  color: ${({ theme }) => theme.colors.menuBg};
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.dashboard.surfaceSubtle};
    border-color: ${({ theme }) => theme.dashboard.infoSoft};
    color: ${({ theme }) => theme.colors.menuBg};
    text-decoration: none;
  }

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.primary};
    font-size: ${({ theme }) => theme.fontSize.xs}rem;
  }

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const ActionLink = styled(Link)`
  ${actionStyles}
`

const ActionButton = styled.button`
  ${actionStyles}
`
